import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { SlotType } from './slot-type.model';

export class ShopCategory {
  name!: string;
  subCategories!: ShopSubCategory[];

  constructor(
    private jsonReaderService: JsonReaderService,
    private userSettingsService: UserSettingsService
  ) {}

  get displayName(): string {
    return this.jsonReaderService.getShopCategoryLocalizedName(this.name, this.userSettingsService.getUserLanguage()) ?? '';
  }
}

export class ShopSubCategory {
  name!: string;
  shopCategory!: ShopCategory;

  constructor(
    private jsonReaderService: JsonReaderService,
    private userSettingsService: UserSettingsService
  ) {}

  get displayName(): string {
    return this.jsonReaderService.getShopSubCategoryLocalizedName(this.name, this.userSettingsService.getUserLanguage()) ?? '';
  }
}

export class Item {
  uniqueName!: string;
  originalUniqueName!: string;
  groupUniqueName!: string;
  shopCategory!: string;
  shopSubCategory!: string;
  craftingCategory: string | undefined;
  unlockedToCraft?: boolean;
  resourceType?: string;
  fameValue?: number;
  itemValue?: number;
  tier?: number;
  weight?: number;
  durability?: number;
  enchantmentLevel?: number;
  baseLootAmount?: number;
  craftingRequirements!: CraftingRequirements[];
  upgradeRequirements!: UpgradeResource[];
  craftFameFactor?: number;
  enchantments!: Enchantment[];
  itemPower?: number;
  combatSpechAchievement!: string;
  maxQualityLevel?: number;
  journalCraftItemFame?: JournalCraftItemFame;
  journalMaxFame?: number;
  slotType?: SlotType;
  journalLoots?: JournalLoot[];

  // FARM PROPS

  unlockedToPlace?: boolean;
  foodCategory?: string;
  nutrition?: number;
  farmPlaceFame?: number;
  farmKind?: string;
  farmActiveFarmFocusCost?: number;
  farmActiveFarmMaxCycles?: number;
  farmActiveFarmActionDurationSeconds?: number;
  farmActiveFarmCycleLengthSeconds?: number;
  farmActiveFarmBonus?: number;
  farmHarvest?: Harvest;
  farmGrownItem?: FarmGrownItem;
  farmConsumption?: FarmConsumption;
  farmProducts?: FarmProducts;

  constructor(
    private jsonReaderService: JsonReaderService,
    private userSettingsService: UserSettingsService
  ) {}

  get localizedName(): string {
    let localizedName = this.jsonReaderService.getItemLocalizedName(this.originalUniqueName, this.userSettingsService.getUserLanguage());
    return localizedName ? `${localizedName} [${this.tier}.${this.enchantmentLevel}]` : '';
  }

  get shopCatSubCat(): string {
    return `${this.shopCategory.toLowerCase()}:${this.shopSubCategory.toLowerCase()}`;
  }

  clone(): Item {
    let cloned = new Item(this.jsonReaderService, this.userSettingsService);
    Object.assign(cloned, this);
    return cloned;
  }
}

export interface FarmFoodSpec {
  nutritionMax: number;
  secondsPerNutrition: number;
  lossBeforeHungry?: number;
  acceptedFood: {
    foodCategory: string;
    favorite?: string;
    favoriteItem?: Item;
    favoriteBonus?: number;
  };
}

export interface FarmConsumption {
  food: FarmFoodSpec;
}

export interface FarmProducts {
  product: FarmProduct;
}

export interface FarmProduct {
  productionTime: number;
  lootList: LootList;
  lootChance: number;
  fame: number;
}

export interface FarmOffspring {
  chance: number;
  amount: number;
}

export interface FarmGrownItem {
  uniqueName: string;
  item?: Item;
  growTime: number;
  fame: number;
  offspring: FarmOffspring;
}

export interface Seed {
  chance: number;
  amount: number;
}

export interface Harvest {
  growTime: number;
  lootList: LootList;
  lootChance: number;
  fame: number;
  seed: Seed;
}

export interface LootItem {
  type: string;
  item?: Item;
  chance: number;
  amountMin: number;
  amountMax: number;
}

export interface LootList {
  name: string;
  lootItems: LootItem[];
}

export interface JournalLoot {
  uniqueName?: string;
  item?: Item;
  amount: number;
  silverAmount: number;
  weight: number;
  probability: number;
}

export interface CraftingRequirements {
  silver?: number;
  gold?: number;
  time?: number;
  craftingFocus?: number;
  amountCrafted?: number;
  swapTransaction: boolean;
  craftResources: CraftResource[];
}

export interface CraftResource {
  ingredientItem?: Item;
  uniqueName: string;
  enchantmentLevel?: number;
  count: number;
  maxReturnAmount?: number;
}

export interface UpgradeRequirements {
  upgradeResources: UpgradeResource[];
}

export interface UpgradeResource {
  uniqueName: string;
  item?: Item;
  count: number;
  price: number;
}

export interface Enchantment {
  enchantmentLevel: number;
  itemPower?: number;
  craftingRequirements: CraftingRequirements[];
  upgradeRequirements: UpgradeRequirements[];
}

export interface JournalCraftItemFame {
  minTier?: number;
  value?: number;
  validItemsUniqueNames: string[];
  validItems: Item[];
}
